// @ts-nocheck
import React from 'react';

export interface BreadcrumbItem {
  key: string;
  label: string;
}

interface BreadcrumbsProps {
  trail: BreadcrumbItem[];
  onNavigate: (viewKey: string) => void;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ trail, onNavigate }) => {
  if (trail.length === 0) {
    return null;
  }

  const handleBack = () => {
    if (trail.length > 1) {
      const previousItem = trail[trail.length - 2];
      onNavigate(previousItem.key);
    } else {
      onNavigate('map');
    }
  };

  // Truncate trail if more than 4 items: show "Map / ... / Parent / Current"
  const shouldCollapse = trail.length > 4;
  const displayTrail = shouldCollapse
    ? [trail[0], { key: 'ellipsis', label: '...' }, trail[trail.length - 2], trail[trail.length - 1]]
    : trail;

  return (
    <nav
      aria-label="Breadcrumb"
      className="animate-fadeIn opacity-0 [animation:fadeIn_0.3s_ease-in_forwards] py-2"
    >
      <div className="flex items-center gap-2 text-xs sm:text-sm overflow-x-auto scrollbar-hide">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="flex-shrink-0 flex items-center justify-center w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-bg-elevated hover:bg-bg-raised text-fg-secondary hover:text-fg-primary transition-colors min-h-11 min-w-11"
          aria-label="Go back"
          title="Go back"
        >
          <span className="text-lg sm:text-base">←</span>
        </button>

        {/* Breadcrumb trail with proper ARIA structure */}
        <ol className="flex items-center gap-2" role="list">
          {displayTrail.map((item, index) => {
            const isLast = index === displayTrail.length - 1;
            const isEllipsis = item.key === 'ellipsis';

            return (
              <li key={item.key} className="flex items-center gap-2">
                {/* Separator before non-first items */}
                {index > 0 && (
                  <span className="text-fg-muted" aria-hidden="true">
                    /
                  </span>
                )}

                {isEllipsis ? (
                  // Ellipsis for collapsed middle items
                  <span className="text-fg-muted px-1 cursor-default">
                    {item.label}
                  </span>
                ) : isLast ? (
                  // Current page (non-clickable)
                  <span
                    aria-current="page"
                    className={
                      index === 0
                        ? 'text-fg-primary font-medium whitespace-nowrap text-xs sm:text-sm inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 p-2 sm:p-0'
                        : 'text-fg-primary font-medium whitespace-nowrap text-xs sm:text-sm'
                    }
                  >
                    {/* Home icon for first item on mobile, text on larger screens */}
                    {index === 0 ? (
                      <>
                        <svg
                          className="w-5 h-5 sm:hidden"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M12 3.172 3 10.5V21h6v-6h6v6h6V10.5L12 3.172z" />
                        </svg>
                        <span className="hidden sm:inline">{item.label}</span>
                      </>
                    ) : (
                      item.label
                    )}
                  </span>
                ) : (
                  // Ancestor link (clickable)
                  <button
                    onClick={() => onNavigate(item.key)}
                    className={
                      index === 0
                        ? 'text-fg-muted hover:text-fg-primary focus:text-fg-primary rounded transition-colors whitespace-nowrap text-xs sm:text-sm inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 p-2 sm:p-0'
                        : 'text-fg-muted hover:text-fg-primary focus:text-fg-primary rounded transition-colors whitespace-nowrap text-xs sm:text-sm'
                    }
                    aria-label={index === 0 ? item.label : undefined}
                  >
                    {/* Home icon for first item on mobile, text on larger screens */}
                    {index === 0 ? (
                      <>
                        <svg
                          className="w-5 h-5 sm:hidden"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M12 3.172 3 10.5V21h6v-6h6v6h6V10.5L12 3.172z" />
                        </svg>
                        <span className="hidden sm:inline">{item.label}</span>
                      </>
                    ) : (
                      item.label
                    )}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </nav>
  );
};
